/* DWC media repair 9. Variant galleries use the assigned image plus SKU-tagged media. */
if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      const start = () => { if (this.isConnected && !this.initialised) this.init(); };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true });
      } else queueMicrotask(start);
    }

    init() {
      this.viewer = this.querySelector('.media-viewer');
      this.section = this.closest('.js-product');
      if (!this.viewer || !this.section) return;
      this.initialised = true;
      this.dataset.dwcJsBuild = '9';
      this.variantImageOnlyEnabled = this.dataset.variantImageOnlyEnabled === 'true';
      this.noSelectedVariant = this.dataset.noSelectedVariant === 'true';
      // A dash before a digit is NOT camel-cased by dataset. Read the exact attribute.
      this.product3dViewEnabled = this.getAttribute('data-product-3d-view-enabled') === 'true';
      this.mediaGroupingEnabled = false;
      this.stackedScroll = this.dataset.stackedScroll || 'never';
      this.stackedUnderline = this.dataset.stackedUnderline === 'true';
      this.viewerItems = Array.from(this.viewer.children).filter((el) => el.classList.contains('media-viewer__item'));
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.thumbs ? Array.from(this.thumbs.children) : [];
      this.thumbsContainer = this.querySelector('.media-gallery__thumbs');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.controls?.querySelector('[name="prev"]');
      this.nextBtn = this.controls?.querySelector('[name="next"]');
      this.counterCurrent = this.controls?.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.controls?.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');
      this.threeDToggleButton = this.querySelector('.js-media-3d-toggle');
      this.threeDNotice = this.querySelector('.js-media-3d-notice');
      this.threeDRepository = this.querySelector('.js-media-3d-repository');
      this.threeDModelSources = new Map();
      this.querySelectorAll('.js-media-3d-source').forEach((source) => {
        const key = this.normalise3DKey(source.dataset.modelKey);
        if (key && !this.threeDModelSources.has(key)) this.threeDModelSources.set(key, source);
      });
      this.wants3D = false;
      this.is3DViewActive = false;
      this.modelRequestId = 0;
      this.transitionId = 0;
      this.restoreTransitionOrder = null;
      this.modelView = null;
      this.activeVariantMedia = this.viewerItems.find((item) => item.dataset.mediaId === this.dataset.initialMediaId)
        || this.viewerItems.find((item) => item.classList.contains('is-current-variant')) || this.viewerItems[0];
      this.activeVariantSku = this.normaliseMediaKey(this.dataset.initialVariantSku);
      this.currentItem = this.activeVariantMedia;
      this.abort = new AbortController();
      const listener = { signal: this.abort.signal };
      this.section.addEventListener('on:variant:change', (event) => this.onVariantChange(event), listener);
      this.viewer.addEventListener('scroll', () => this.handleScroll(), { ...listener, passive: true });
      this.controls?.addEventListener('click', (event) => this.handleNavClick(event), listener);
      this.thumbs?.addEventListener('click', (event) => this.handleThumbClick(event), listener);
      this.threeDToggleButton?.addEventListener('click', () => this.handle3DToggleClick(), listener);
      window.addEventListener('on:debounced-resize', () => this.handleResize(), listener);
      this.viewer.addEventListener('click', (event) => this.handleZoomClick(event), listener);
      this.viewer.addEventListener('mousemove', (event) => this.handleZoomMouseMove(event), listener);
      const modalTemplate = this.querySelector('.js-media-zoom-template');
      if (this.hasAttribute('data-zoom-enabled') && modalTemplate?.content.firstElementChild) {
        this.galleryModal = modalTemplate.content.firstElementChild.cloneNode(true);
      }
      this.applyVisibility(this.allowedItems(), this.currentItem);
      this.sync3DView();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.transitionId += 1;
      this.modelRequestId += 1;
      this.restoreTransitionOrder?.();
      this.restore3D();
      if (this.galleryModal?.isConnected) this.galleryModal.remove();
      this.initialised = false;
    }

    normaliseMediaKey(value) { return String(value || '').trim().toLowerCase(); }
    normalise3DKey(value) { return this.normaliseMediaKey(value); }
    isStacked() { return this.dataset.layout === 'stacked' && Boolean(window.theme?.mediaMatches?.md); }
    allowedItems() {
      if (this.noSelectedVariant) {
        return this.variantImageOnlyEnabled ? this.viewerItems.slice(0, 1) : this.viewerItems;
      }
      if (!this.variantImageOnlyEnabled || !this.activeVariantMedia) return this.viewerItems;
      return this.viewerItems.filter((item) =>
        item === this.activeVariantMedia
        || Boolean(this.activeVariantSku && String(item.dataset.mediaKey || '').split(',')
          .some((sku) => this.normaliseMediaKey(sku) === this.activeVariantSku)));
    }

    /** Set a complete visibility state. OFF restores every media item and thumbnail. */
    applyVisibility(items, target, updateUi = true) {
      const visible = new Set(items);
      const ids = new Set(items.map((item) => item.dataset.mediaId));
      this.viewerItems.forEach((item) => {
        item.hidden = !visible.has(item);
        item.style.display = visible.has(item) ? 'block' : 'none';
        item.classList.remove('media-viewer__item--variant');
        item.classList.toggle('media-viewer__item--single', items.length === 1);
        item.style.marginInlineEnd = items.length === 1 ? '0px' : '';
      });
      this.thumbsItems.forEach((item) => {
        item.hidden = !ids.has(item.dataset.mediaId);
        item.style.display = ids.has(item.dataset.mediaId) ? 'block' : 'none';
        item.classList.remove('media-thumbs__item--variant');
      });
      this.visibleItems = this.viewerItems.filter((item) => visible.has(item));
      this.currentItem = visible.has(target) ? target : this.visibleItems[0];
      if (updateUi) this.updateControls();
      if (this.currentItem && !this.isStacked()) this.jumpTo(this.currentItem);
    }

    updateControls() {
      this.currentIndex = Math.max(0, this.visibleItems.indexOf(this.currentItem));
      const multiple = this.visibleItems.length > 1;
      this.setControlVisibility(this.controls, multiple);
      this.setControlVisibility(this.thumbsContainer, multiple);
      if (this.prevBtn) this.prevBtn.disabled = !multiple || this.currentIndex === 0;
      if (this.nextBtn) this.nextBtn.disabled = !multiple || this.currentIndex >= this.visibleItems.length - 1;
      if (this.counterCurrent) this.counterCurrent.textContent = this.visibleItems.length ? this.currentIndex + 1 : 0;
      if (this.counterTotal) this.counterTotal.textContent = this.visibleItems.length;
      this.setActiveThumb();
    }
    setControlVisibility(element, visible) {
      if (!element) return;
      element.hidden = false;
      element.classList.toggle('is-inactive', !visible);
      element.setAttribute('aria-hidden', String(!visible));
      if ('inert' in element) element.inert = !visible;
    }
    leftFor(item) { return this.viewer.scrollLeft + item.getBoundingClientRect().left - this.viewer.getBoundingClientRect().left; }
    jumpTo(item) {
      const saved = this.viewer.style.scrollBehavior;
      this.viewer.style.scrollBehavior = 'auto';
      this.viewer.scrollTo({ left: this.leftFor(item), behavior: 'instant' });
      this.viewer.style.scrollBehavior = saved;
    }

    /** During a filtered variant change, only outgoing/incoming slides may be visible. */
    transitionTo(target) {
      const previous = this.currentItem;
      const token = ++this.transitionId;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!previous || previous === target || this.isStacked() || reduced) {
        this.isTransitioning = false;
        this.applyVisibility(this.allowedItems(), target);
        this.sync3DView();
        return;
      }
      this.isTransitioning = true;
      this.modelRequestId += 1;
      this.restore3D();
      this.restoreTransitionOrder?.();
      const originalParent = target.parentNode;
      const originalNextSibling = target.nextSibling;
      previous.after(target);
      this.restoreTransitionOrder = () => {
        if (!originalParent) return;
        originalParent.insertBefore(target, originalNextSibling);
        this.restoreTransitionOrder = null;
      };
      this.applyVisibility([previous, target], previous, false);
      if (this.threeDToggleButton) this.threeDToggleButton.disabled = true;
      const snap = this.viewer.style.scrollSnapType;
      this.viewer.style.scrollSnapType = 'none';
      this.jumpTo(previous);
      requestAnimationFrame(() => {
        if (token !== this.transitionId) return;
        const startLeft = this.viewer.scrollLeft;
        const endLeft = this.leftFor(target);
        const started = performance.now();
        const duration = 250;
        const move = (now) => {
          if (token !== this.transitionId || !this.isConnected) return;
          const progress = Math.min(1, (now - started) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          this.viewer.scrollTo({ left: startLeft + (endLeft - startLeft) * eased, behavior: 'auto' });
          if (progress >= 1) {
            this.isTransitioning = false;
            this.viewer.style.scrollSnapType = snap;
            this.restoreTransitionOrder?.();
            this.applyVisibility(this.allowedItems(), target);
            this.sync3DView();
          } else requestAnimationFrame(move);
        };
        requestAnimationFrame(move);
      });
    }

    onVariantChange(event) {
      if (event.target.closest('.js-product') !== this.section) return;
      const variant = event.detail?.variant;
      if (!variant) return;
      this.applyVariantChange(variant);
    }

    applyVariantChange(variant) {
      const id = variant?.featured_media?.id || variant?.featured_image?.id;
      const target = this.viewerItems.find((item) => item.dataset.mediaId === String(id))
        || this.viewerItems.find((item) => item.dataset.mediaType === 'image');
      if (!target) return;
      this.modelRequestId += 1;
      this.noSelectedVariant = false;
      this.dataset.noSelectedVariant = 'false';
      if (this.isTransitioning) {
        this.transitionId += 1;
        this.isTransitioning = false;
        this.viewer.style.scrollSnapType = '';
        this.restoreTransitionOrder?.();
        const viewerLeft = this.viewer.getBoundingClientRect().left;
        const nearestItem = this.visibleItems.reduce((closest, candidate) =>
          Math.abs(candidate.getBoundingClientRect().left - viewerLeft) < Math.abs(closest.getBoundingClientRect().left - viewerLeft)
            ? candidate
            : closest);
        this.applyVisibility([nearestItem], nearestItem);
      }
      this.activeVariantMedia = target;
      this.activeVariantSku = this.normaliseMediaKey(variant?.sku);
      if (this.variantImageOnlyEnabled) this.transitionTo(target);
      else {
        this.restore3D();
        this.applyVisibility(this.viewerItems, this.currentItem);
        this.customSetActiveMedia(target, true);
      }
    }

    customSetActiveMedia(item, scrollToItem = true) {
      if (!item || !this.visibleItems.includes(item)) return;
      if (this.currentItem !== item && typeof window.pauseAllMedia === 'function') window.pauseAllMedia(this);
      this.currentItem = item;
      if (this.isStacked()) {
        this.viewerItems.forEach((slide) => slide.classList.toggle('is-active', slide === item && this.stackedUnderline));
        if (scrollToItem && this.stackedScroll !== 'never') {
          const top = item.getBoundingClientRect().top + window.scrollY - 150;
          if (Math.abs(top - window.scrollY) > 300) window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
      } else if (scrollToItem) this.viewer.scrollTo({ left: this.leftFor(item), behavior: 'smooth' });
      this.updateControls();
      this.sync3DView();
      if (this.liveRegion) this.liveRegion.textContent = `Media ${this.currentIndex + 1} of ${this.visibleItems.length}`;
    }
    // Compatibility with the theme's variant-picker interface.
    setActiveMedia() {}

    handleScroll() {
      if (this.isTransitioning || this.isStacked() || this.visibleItems.length < 2) return;
      const left = this.viewer.getBoundingClientRect().left;
      const item = this.visibleItems.reduce((closest, candidate) =>
        Math.abs(candidate.getBoundingClientRect().left - left) < Math.abs(closest.getBoundingClientRect().left - left) ? candidate : closest);
      if (item !== this.currentItem) this.customSetActiveMedia(item, false);
    }
    handleNavClick(event) {
      const button = event.target.closest('.media-ctrl__btn');
      if (!button || button.disabled || this.isTransitioning) return;
      const item = this.visibleItems[this.currentIndex + (button.name === 'next' ? 1 : -1)];
      if (item) this.viewer.scrollTo({ left: this.leftFor(item), behavior: 'smooth' });
    }
    handleThumbClick(event) {
      const thumb = event.target.closest('.media-thumbs__item');
      if (!thumb || thumb.hidden || this.isTransitioning) return;
      const item = this.visibleItems.find((slide) => slide.dataset.mediaId === thumb.dataset.mediaId);
      if (!item) return;
      this.customSetActiveMedia(item);
      if (item.dataset.mediaType !== 'image') MediaGallery.playActiveMedia(item);
    }
    handleResize() {
      if (this.isTransitioning) return;
      this.updateControls();
      if (this.currentItem && !this.isStacked()) this.jumpTo(this.currentItem);
    }
    setActiveThumb() {
      this.thumbsItems.forEach((thumb) => {
        const button = thumb.querySelector('.media-thumbs__btn');
        if (!button) return;
        const active = thumb.dataset.mediaId === this.currentItem?.dataset.mediaId;
        button.classList.toggle('is-active', active);
        if (active) { button.setAttribute('aria-current', 'true'); this.currentThumb = thumb; }
        else button.removeAttribute('aria-current');
      });
      if (this.thumbs && this.currentThumb && !this.currentThumb.hidden) {
        const left = this.currentThumb.offsetLeft;
        if (left < this.thumbs.scrollLeft || left + this.currentThumb.offsetWidth > this.thumbs.scrollLeft + this.thumbs.clientWidth) {
          this.thumbs.scrollTo({ left, behavior: 'smooth' });
        }
      }
    }

    handle3DToggleClick() {
      if (this.threeDToggleButton.disabled || this.isTransitioning) return;
      this.wants3D = !this.wants3D;
      this.sync3DView();
    }
    set3DState(state, message = '') {
      this.dataset.dwc3dState = state;
      this.is3DViewActive = state === 'ready';
      if (this.threeDToggleButton) {
        this.threeDToggleButton.setAttribute('aria-pressed', String(state === 'ready'));
        this.threeDToggleButton.setAttribute('aria-checked', String(state === 'ready'));
        this.threeDToggleButton.setAttribute('aria-busy', String(state === 'loading'));
      }
      if (this.threeDNotice) { this.threeDNotice.hidden = !message; this.threeDNotice.textContent = message; }
    }
    restore3D() {
      if (!this.modelView) return;
      const { source, imageLayer } = this.modelView;
      try { source.querySelector('product-model')?.modelViewerUI?.pause(); } catch (error) { /* Already detached. */ }
      source.hidden = true;
      source.classList.remove('js-media-3d-runtime-layer');
      source.style.removeProperty('min-height');
      this.threeDRepository?.appendChild(source);
      imageLayer.hidden = false;
      imageLayer.style.removeProperty('display');
      this.modelView = null;
    }

    async sync3DView() {
      if (!this.product3dViewEnabled || !this.threeDToggleButton) return;
      const item = this.currentItem;
      const key = item?.dataset.mediaType === 'image' ? this.activeVariantSku : '';
      const source = this.threeDModelSources.get(key);
      const imageLayer = item?.querySelector('.js-media-3d-image-layer');
      const model = source?.querySelector('product-model');
      const hasMatch = Boolean(key && source && imageLayer && model);
      this.threeDToggleButton.hidden = !hasMatch;
      this.threeDToggleButton.style.display = hasMatch ? '' : 'none';
      this.threeDToggleButton.disabled = !hasMatch;
      this.threeDToggleButton.title = hasMatch ? 'Show the matching interactive 3D model' : `No matching 3D model for ${key || 'this media'}`;
      this.dataset.dwc3dImageKey = key;
      if (this.xrButton) this.xrButton.hidden = !this.wants3D || !hasMatch;
      if (!hasMatch) {
        this.wants3D = false;
        this.modelRequestId += 1;
        this.restore3D();
        this.set3DState('off');
        return;
      }
      if (!this.wants3D) {
        this.modelRequestId += 1;
        this.restore3D();
        this.set3DState('off');
        return;
      }
      if (this.modelView?.source === source && this.modelView?.item === item) return;
      const request = ++this.modelRequestId;
      this.restore3D();
      const height = imageLayer.getBoundingClientRect().height;
      this.modelView = { source, imageLayer, item };
      imageLayer.hidden = true;
      imageLayer.style.setProperty('display', 'none', 'important');
      source.hidden = false;
      source.classList.add('js-media-3d-runtime-layer');
      if (height > 0) source.style.minHeight = `${height}px`;
      item.appendChild(source);
      if (this.xrButton) this.xrButton.dataset.shopifyModel3dId = source.dataset.modelId;
      this.set3DState('loading', 'Loading 3D model...');
      try {
        await new Promise((resolve, reject) => {
          const timer = setTimeout(() => reject(new Error('The product-model script did not initialise.')), 10000);
          customElements.whenDefined('product-model').then(() => { clearTimeout(timer); resolve(); }, reject);
        });
        if (request !== this.modelRequestId || !this.wants3D || !this.isConnected) return;
        const loadTask = model.loadContent(false, true, '3d-toggle');
        if (!loadTask || typeof loadTask.then !== 'function') throw new Error('Update assets/product-model.js from this repair pack.');
        const viewer = await loadTask;
        if (request !== this.modelRequestId || !this.wants3D || this.currentItem !== item || !this.isConnected) {
          if (model.closest('.js-media-3d-repository')) model.modelViewerUI?.pause();
          return;
        }
        if (!viewer || viewer.loaded !== true) throw new Error('The model viewer has not confirmed a loaded model.');
        this.set3DState('ready');
      } catch (error) {
        if (request !== this.modelRequestId || !this.isConnected) return;
        this.wants3D = false;
        this.restore3D();
        this.set3DState('error', `3D could not load. ${error.message}`);
        if (this.xrButton) this.xrButton.hidden = true;
        console.error('[DWC 3D]', { variantSku: key, modelId: source.dataset.modelId, error });
      }
    }

    handleZoomClick(event) {
      const link = event.target.closest('.js-zoom-link');
      if (!link) return;
      event.preventDefault();
      if (this.is3DViewActive || this.wants3D) return;
      if (this.dataset.zoomTrigger === 'click') this.loadZoomImage(link);
    }
    loadZoomImage(link) {
      const zoom = link.querySelector('.js-zoom-image');
      if (!zoom?.dataset.src || zoom.dataset.dwcLoading) return;
      zoom.dataset.dwcLoading = 'true';
      this.loadingSpinner?.classList.remove('loading-spinner--out');
      const image = new Image();
      image.onload = () => {
        zoom.src = image.src;
        zoom.removeAttribute('data-src');
        delete zoom.dataset.dwcLoading;
        link.classList.remove('media--zoom-not-loaded');
        this.loadingSpinner?.classList.add('loading-spinner--out');
      };
      image.onerror = () => { delete zoom.dataset.dwcLoading; this.loadingSpinner?.classList.add('loading-spinner--out'); };
      image.src = zoom.dataset.src;
    }
    handleZoomMouseMove(event) {
      if (!window.theme?.mediaMatches?.md || this.wants3D) return;
      const link = event.target.closest('.js-zoom-link');
      if (!link || !this.dataset.zoomTrigger) return;
      if (this.dataset.zoomTrigger === 'hover') this.loadZoomImage(link);
      const zoom = link.querySelector('.js-zoom-image');
      if (!zoom) return;
      const box = link.getBoundingClientRect();
      if (box.width && box.height) zoom.style.objectPosition = `${(event.clientX - box.left) / box.width * 100}% ${(event.clientY - box.top) / box.height * 100}%`;
    }
    static playActiveMedia(item) {
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      const model = item.querySelector('product-model');
      if (model?.loadContent) model.loadContent(false)?.catch((error) => console.error('[DWC model]', error));
      item.querySelector('deferred-media')?.loadContent();
    }
  }
  customElements.define('media-gallery', MediaGallery);
}
